import { useEffect, useState } from "react";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import Inbox from "../components/Inbox";
import NextTripHero from "../components/dashboard/NextTripHero";
import Act60Gauge from "../components/dashboard/Act60Gauge";
import TimelineStrip from "../components/dashboard/TimelineStrip";
import ForwardingFunnel from "../components/dashboard/ForwardingFunnel";
import TripRows from "../components/dashboard/TripRows";
import WishlistRadar from "../components/dashboard/WishlistRadar";
import BudgetSnapshot from "../components/dashboard/BudgetSnapshot";
import DocumentAlerts from "../components/dashboard/DocumentAlerts";
import TodayCockpit from "../components/home/TodayCockpit";
import StatStrip from "../components/home/StatStrip";
import UpNext from "../components/home/UpNext";
import PullToRefresh from "../components/home/PullToRefresh";
import { toast } from "sonner";
import { ChevronDown, ChevronRight } from "lucide-react";

const HOME_CACHE = "wandr_home_state";
const OVERRIDE_KEY = "wandr_home_view_override";

function Skeleton() {
  return (
    <div className="grid grid-cols-12 gap-4" data-testid="dashboard-skeleton">
      <div className="col-span-12 lg:col-span-8 rounded-card border border-hairline bg-surface h-72 animate-pulse" />
      <div className="col-span-12 lg:col-span-4 rounded-card border border-hairline bg-surface h-72 animate-pulse" />
      <div className="col-span-12 rounded-card border border-hairline bg-surface h-32 animate-pulse" />
      <div className="col-span-12 lg:col-span-8 rounded-card border border-hairline bg-surface h-56 animate-pulse" />
      <div className="col-span-12 lg:col-span-4 rounded-card border border-hairline bg-surface h-56 animate-pulse" />
    </div>
  );
}

function DashGrid({ data, user, load }) {
  return (
    <div className="grid grid-cols-12 gap-4">
      <div className="col-span-12 md:hidden fade-up fade-up-1">
        <StatStrip data={data} />
      </div>
      <div className={`${data.next_trip ? "hidden md:block" : ""} col-span-12 lg:col-span-8 fade-up fade-up-1`}>
        <NextTripHero trip={data.next_trip} reservations={data.next_trip_reservations} today={data.today} />
      </div>
      <div className="hidden md:block col-span-12 lg:col-span-4 fade-up fade-up-2">
        <Act60Gauge act60={data.act60} today={data.today} />
      </div>
      <div className="col-span-12 fade-up fade-up-3">
        <TimelineStrip trips={data.trips} today={data.today} />
      </div>
      <div className="col-span-12 md:hidden fade-up fade-up-3">
        <UpNext trips={data.trips} today={data.today} />
      </div>
      <div className="col-span-12 fade-up fade-up-4">
        <BudgetSnapshot />
      </div>
      <div className="col-span-12 fade-up fade-up-4">
        <ForwardingFunnel email={user?.forwarding_email} funnel={data.funnel} usage={data.usage} />
      </div>
      <div className="hidden md:block col-span-12 lg:col-span-8 fade-up fade-up-5">
        <TripRows trips={data.trips} today={data.today} />
      </div>
      <div className="hidden md:block col-span-12 lg:col-span-4 fade-up fade-up-6">
        <WishlistRadar items={data.wishlist} />
      </div>
      <div className="col-span-12 fade-up fade-up-6">
        <Inbox onChanged={load} />
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [home, setHome] = useState(() => {
    try { return JSON.parse(localStorage.getItem(HOME_CACHE)) || null; } catch { return null; }
  });
  const [view, setView] = useState(null); // null = auto
  const [showPlanning, setShowPlanning] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/dashboard");
      setData(data);
    } catch (e) {
      toast.error("Could not load dashboard");
    }
  };

  const loadHome = async () => {
    try {
      const { data } = await api.get("/home/state");
      setHome(data);
      try { localStorage.setItem(HOME_CACHE, JSON.stringify(data)); } catch {}
    } catch {
      /* offline — keep cached payload so the cockpit still renders */
    }
  };

  useEffect(() => { load(); loadHome(); }, []);

  const state = home?.state || "planning";
  const activeTrip = home?.active_trip || null;
  const hasActive = !!activeTrip && (state === "live" || state === "imminent");

  useEffect(() => {
    if (!activeTrip) return;
    try {
      const o = JSON.parse(localStorage.getItem(OVERRIDE_KEY));
      if (o && o.tripId === activeTrip.id && (o.view === "today" || o.view === "overview")) setView(o.view);
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTrip?.id]);

  const autoView = state === "live" ? "today" : "overview";
  const effView = hasActive ? (view || autoView) : "overview";
  const setViewPersist = (v) => {
    setView(v);
    try { localStorage.setItem(OVERRIDE_KEY, JSON.stringify({ tripId: activeTrip?.id, view: v })); } catch {}
  };

  const todayLabel = new Date().toLocaleDateString(undefined, {
    weekday: "short", month: "short", day: "numeric", year: "numeric",
  });

  return (
    <div>
      <PullToRefresh onRefresh={() => Promise.all([load(), loadHome()])} />
      <main className="max-w-6xl mx-auto px-6 md:px-12 lg:px-16 pt-6 md:pt-12 pb-28 md:pb-12" data-testid="dashboard">
        <div className="mb-8 hidden md:flex items-end justify-between gap-4">
          <div>
            <p className="label-tag mb-3">Command Center</p>
            <h1 className="heading-h1 text-4xl sm:text-5xl lg:text-6xl">
              Hello{user?.name ? `, ${user.name}` : ""}.
            </h1>
            <div className="divider-strong mt-4" />
          </div>
          <p className="font-mono text-xs text-sand-500 uppercase tracking-[0.15em] pb-2 hidden sm:block" data-testid="dashboard-date">
            {todayLabel}
          </p>
        </div>

        {hasActive && (
          <div className="mb-5 inline-flex border border-hairline rounded-full p-0.5 bg-surface" data-testid="home-view-toggle">
            {["today", "overview"].map((v) => (
              <button key={v} onClick={() => setViewPersist(v)}
                className={`rounded-full px-4 py-1.5 text-xs font-semibold capitalize transition-colors ${
                  effView === v ? "bg-ink text-white" : "text-sand-600 hover:text-ink"}`}
                data-testid={`home-view-${v}`}>
                {v}
              </button>
            ))}
          </div>
        )}

        <DocumentAlerts />

        {effView === "today" && activeTrip ? (
          <>
            <TodayCockpit home={home} variant={state === "live" ? "full" : "imminent"} />
            <div className="mt-6">
              <button onClick={() => setShowPlanning((v) => !v)}
                className="inline-flex items-center gap-1.5 font-mono text-[11px] font-semibold uppercase tracking-wider text-sand-500 hover:text-ink transition-colors"
                data-testid="planning-strip-toggle">
                {showPlanning ? <ChevronDown size={13} /> : <ChevronRight size={13} />} Planning overview
              </button>
              {showPlanning && (
                <div className="mt-4">
                  {data === null ? <Skeleton /> : <DashGrid data={data} user={user} load={load} />}
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            {activeTrip && state === "live" && (
              <button onClick={() => setViewPersist("today")}
                className="w-full mb-4 flex items-center gap-2 rounded-full border border-ember/40 bg-ember/5 px-4 py-2 text-left text-sm text-ink hover:bg-ember/10 transition-colors"
                data-testid="live-peek">
                <span className="w-2 h-2 bg-ember rounded-full animate-pulse shrink-0" />
                <span className="truncate">
                  You're on <span className="font-semibold">{activeTrip.name}</span>
                  {home?.day_x_of_y ? ` — Day ${home.day_x_of_y.x} of ${home.day_x_of_y.y}` : ""}
                </span>
                <span className="ml-auto font-mono text-[10px] uppercase tracking-wide text-ember shrink-0">Today →</span>
              </button>
            )}
            {activeTrip && state === "imminent" && <TodayCockpit home={home} variant="imminent" />}
            {data === null ? <Skeleton /> : <DashGrid data={data} user={user} load={load} />}
          </>
        )}
      </main>
    </div>
  );
}
